import React, { useState, useEffect, useCallback } from 'react';

// ============================================================
// DROP 49 — CONTACTS DIRECTORY CRM
// Administration group — staff-facing
// Primary: PostgreSQL | Secondary: JSON DB sync
// ============================================================

const C = {
  bg:       '#070D0A',
  surface:  '#0E1812',
  card:     '#121E16',
  border:   '#1C2E22',
  text:     '#EDE8DC',
  muted:    'rgba(237,232,220,0.5)',
  green:    '#0F5132',
  gold:     '#C9A84C',
  goldLt:   '#E2C97E',
  red:      '#ef4444',
  blue:     '#60a5fa',
  teal:     '#2dd4bf',
};

const CONTACT_TYPES = [
  { value: 'BOT',         label: 'Board of Trustees',    emoji: '🏛️', color: C.gold },
  { value: 'CWC',         label: 'CWC Member',           emoji: '⚖️', color: C.teal },
  { value: 'LOC',         label: 'LOC Member',           emoji: '📋', color: C.blue },
  { value: 'VILLAGE_REP', label: 'Village Representative',emoji: '🏘️', color: '#4ade80' },
  { value: 'DIASPORA',    label: 'Diaspora',             emoji: '✈️', color: '#a78bfa' },
  { value: 'VENDOR',      label: 'Vendor / Exhibitor',   emoji: '🛒', color: '#fb923c' },
  { value: 'SPONSOR',     label: 'Sponsor',              emoji: '🤝', color: C.goldLt },
  { value: 'GUEST',       label: 'Guest / Dignitary',    emoji: '👑', color: '#f472b6' },
  { value: 'STAFF',       label: 'Staff Member',         emoji: '🧑‍💼', color: '#94a3b8' },
  { value: 'MEDIA',       label: 'Media',                emoji: '📸', color: '#38bdf8' },
];

const VILLAGES = [
  { id: 'v01', name: 'Jattu-Uzairue' },
  { id: 'v02', name: 'Afashio-Uzairue' },
  { id: 'v03', name: 'Afowa-Uzairue' },
  { id: 'v04', name: 'Apana-Uzairue' },
  { id: 'v05', name: 'Ayaoghena-Uzairue' },
  { id: 'v06', name: 'Ayogwiri-Uzairue' },
  { id: 'v07', name: 'Ayua-Uzairue' },
  { id: 'v08', name: 'Elele-Uzairue' },
  { id: 'v09', name: 'Idatto-Uzairue' },
  { id: 'v10', name: 'Ikabigbo-Uzairue' },
  { id: 'v11', name: 'Ikholo-Uzairue' },
  { id: 'v12', name: 'Imeke-Uzairue' },
  { id: 'v13', name: 'Imhonikhe-Uzairue' },
  { id: 'v14', name: 'Irekpai-Uzairue' },
  { id: 'v15', name: 'Iyamho-Uzairue' },
  { id: 'v16', name: 'Iyora-Uzairue' },
  { id: 'v17', name: 'Iyuku-Uzairue' },
  { id: 'v18', name: 'Ogbido-Uzairue' },
  { id: 'v19', name: 'Ozor-Uzairue' },
  { id: 'v20', name: 'Ugbenor-Uzairue' },
  { id: 'v21', name: 'Uluoke-Uzairue' },
  { id: 'v22', name: 'Yelwa-Uzairue' },
  { id: 'sp01', name: 'Igala Community' },
  { id: 'sp02', name: 'Igbo Community' },
  { id: 'sp03', name: 'Yoruba Community' },
  { id: 'sp04', name: 'Diaspora Community' },
];

const NIGERIAN_STATES = [
  'Abia','Adamawa','Akwa Ibom','Anambra','Bauchi','Bayelsa','Benue','Borno',
  'Cross River','Delta','Ebonyi','Edo','Ekiti','Enugu','FCT','Gombe','Imo',
  'Jigawa','Kaduna','Kano','Katsina','Kebbi','Kogi','Kwara','Lagos','Nasarawa',
  'Niger','Ogun','Ondo','Osun','Oyo','Plateau','Rivers','Sokoto','Taraba',
  'Yobe','Zamfara',
];

type ContactType =
  | 'BOT' | 'CWC' | 'LOC' | 'VILLAGE_REP' | 'DIASPORA'
  | 'VENDOR' | 'SPONSOR' | 'GUEST' | 'STAFF' | 'MEDIA';

interface ContactEntry {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  whatsapp?: string;
  type: ContactType;
  role?: string;
  village?: string;
  community?: string;
  organization?: string;
  address?: string;
  state?: string;
  country: string;
  isActive: boolean;
  isDiaspora: boolean;
  notes?: string;
  tags: string[];
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  festivalYear: number;
}

type TabId = 'ALL' | ContactType;

const EMPTY_FORM: Omit<ContactEntry,'id'|'createdAt'|'updatedAt'> = {
  firstName: '', lastName: '', email: '', phone: '', whatsapp: '',
  type: 'BOT', role: '', village: '', community: '', organization: '',
  address: '', state: '', country: 'Nigeria', isActive: true,
  isDiaspora: false, notes: '', tags: [], createdBy: '', festivalYear: 2026,
};

interface Props {
  currentUser?: { email?: string; systemRole?: string; role?: string; name?: string } | null;
}

const ContactsDirectory: React.FC<Props> = ({ currentUser }) => {
  const [contacts, setContacts]       = useState<ContactEntry[]>([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');
  const [activeTab, setActiveTab]     = useState<TabId>('ALL');
  const [search, setSearch]           = useState('');
  const [filterVillage, setFilterVillage] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterActive, setFilterActive]   = useState<'all'|'active'|'inactive'>('active');
  const [showModal, setShowModal]     = useState(false);
  const [editContact, setEditContact] = useState<ContactEntry | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ContactEntry | null>(null);
  const [form, setForm]               = useState({ ...EMPTY_FORM });
  const [tagInput, setTagInput]       = useState('');
  const [viewContact, setViewContact] = useState<ContactEntry | null>(null);

  const token = localStorage.getItem('erue_auth_token') || sessionStorage.getItem('erue_auth_token') || '';

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/contacts', { headers });
      const data = await res.json();
      setContacts(Array.isArray(data.contacts) ? data.contacts : []);
    } catch {
      setError('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const flash = (msg: string, isErr = false) => {
    if (isErr) setError(msg); else setSuccess(msg);
    setTimeout(() => { setError(''); setSuccess(''); }, 4000);
  };

  const openAdd = () => {
    setEditContact(null);
    setForm({ ...EMPTY_FORM, createdBy: currentUser?.email || '' });
    setTagInput('');
    setShowModal(true);
  };

  const openEdit = (c: ContactEntry) => {
    setEditContact(c);
    setForm({
      firstName: c.firstName, lastName: c.lastName, email: c.email || '',
      phone: c.phone || '', whatsapp: c.whatsapp || '', type: c.type,
      role: c.role || '', village: c.village || '', community: c.community || '',
      organization: c.organization || '', address: c.address || '',
      state: c.state || '', country: c.country, isActive: c.isActive,
      isDiaspora: c.isDiaspora, notes: c.notes || '',
      tags: [...c.tags], createdBy: c.createdBy || '', festivalYear: c.festivalYear,
    });
    setTagInput('');
    setShowModal(true);
    setViewContact(null);
  };

  const handleSave = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      flash('First name and last name are required', true); return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      const url     = editContact ? `/api/contacts/${editContact.id}` : '/api/contacts';
      const method  = editContact ? 'PUT' : 'POST';
      const res     = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      const data    = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      flash(editContact ? 'Contact updated' : 'Contact added');
      setShowModal(false);
      fetchContacts();
    } catch (e: unknown) {
      flash(e instanceof Error ? e.message : 'Save failed', true);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`/api/contacts/${deleteTarget.id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error('Delete failed');
      flash('Contact deleted');
      setDeleteTarget(null);
      fetchContacts();
    } catch (e: unknown) {
      flash(e instanceof Error ? e.message : 'Delete failed', true);
    }
  };

  const handleExportCSV = async () => {
    try {
      const res = await fetch('/api/contacts/export/csv', { headers });
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `erue-contacts-${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { flash('CSV export failed', true); }
  };

  // ── CSV Import handlers ────────────────────────────────────────
  const handleImportCSVTemplate = () => {
    window.open('/api/contacts/csv-template', '_blank');
  };

  const handleImportCSVUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) { flash('Please select a .csv file', true); return; }
    try {
      flash('Parsing CSV...');
      const text = await file.text();
      const parseRow = (line: string): string[] => {
        const result: string[] = []; let current = ''; let inQ = false;
        for (let ci = 0; ci < line.length; ci++) {
          const ch = line[ci];
          if (ch === '"') { if (inQ && line[ci+1]==='"'){current+='"';ci++;}else{inQ=!inQ;} }
          else if (ch === ',' && !inQ) { result.push(current.trim()); current = ''; }
          else { current += ch; }
        }
        result.push(current.trim()); return result;
      };
      const csvLines = text.split(/\r?\n/).filter((l: string) => l.trim());
      if (csvLines.length < 2) { flash('CSV has no data rows', true); return; }
      const headers = parseRow(csvLines[0]);
      const rows = csvLines.slice(1).map((line: string) => {
        const vals = parseRow(line);
        const obj: Record<string,string> = {};
        headers.forEach((h: string, i: number) => { obj[h.trim()] = (vals[i] || '').trim(); });
        return obj;
      }).filter((row: Record<string,string>) => Object.values(row).some(v => v));
      if (rows.length === 0) { flash('No valid rows found', true); return; }
      flash('Uploading ' + rows.length + ' contacts...');
      const tok = localStorage.getItem('erue_auth_token') || sessionStorage.getItem('erue_auth_token') || '';
      const r = await fetch('/api/contacts/import-csv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + tok },
        body: JSON.stringify({ rows }),
      });
      const d = await r.json();
      if (d.success) { flash(d.message || d.imported + ' contacts imported'); fetchContacts(); }
      else { flash(d.error || 'Import failed', true); }
    } catch (err: any) { flash(err.message || 'Import failed', true); }
    e.target.value = '';
  };

  const handleExportPDF = () => {
    window.print();
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.tags.includes(t)) {
      setForm(f => ({ ...f, tags: [...f.tags, t] }));
    }
    setTagInput('');
  };

  const removeTag = (t: string) => setForm(f => ({ ...f, tags: f.tags.filter(x => x !== t) }));

  // Filtered contacts
  const filtered = contacts.filter(c => {
    if (activeTab !== 'ALL' && c.type !== activeTab) return false;
    if (filterVillage && c.village !== filterVillage) return false;
    if (filterActive === 'active' && !c.isActive) return false;
    if (filterActive === 'inactive' && c.isActive) return false;
    const q = search.toLowerCase();
    if (q) {
      const hay = `${c.firstName} ${c.lastName} ${c.email} ${c.phone} ${c.organization} ${c.role} ${c.community}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (filterCountry && c.country !== filterCountry) return false;
    return true;
  });

  // Stats
  const stats = {
    total:    contacts.length,
    active:   contacts.filter(c => c.isActive).length,
    diaspora: contacts.filter(c => c.isDiaspora).length,
    byType:   CONTACT_TYPES.reduce((acc, t) => {
      acc[t.value] = contacts.filter(c => c.type === t.value).length;
      return acc;
    }, {} as Record<string, number>),
  };

  const typeInfo = (type: string) => CONTACT_TYPES.find(t => t.value === type) || CONTACT_TYPES[0];
  const villageName = (id: string) => VILLAGES.find(v => v.id === id)?.name || id;

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', borderRadius: 6,
    border: `1px solid ${C.border}`, background: C.surface,
    color: C.text, fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = { fontSize: 11, color: C.muted, marginBottom: 4, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' };
  const btnPrimary: React.CSSProperties = { padding: '9px 20px', borderRadius: 8, border: 'none', background: C.gold, color: '#000', fontWeight: 700, fontSize: 13, cursor: 'pointer' };
  const btnGhost: React.CSSProperties  = { padding: '9px 20px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'none', color: C.text, fontSize: 13, cursor: 'pointer' };
  const btnDanger: React.CSSProperties = { padding: '9px 20px', borderRadius: 8, border: 'none', background: C.red, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' };

  return (
    <div style={{ background: C.bg, minHeight: '100vh', padding: '24px', fontFamily: "'Plus Jakarta Sans', sans-serif", color: C.text }}>

      {/* HEADER */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: "'Cinzel', serif", fontSize: 22, color: C.goldLt, margin: 0 }}>📋 Contacts Directory</h1>
          <p style={{ color: C.muted, fontSize: 13, margin: '4px 0 0' }}>ERUE Festival of Uzairue Kingdom — CRM</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button onClick={handleExportCSV} style={btnGhost}>⬇ Export CSV</button>
          <button onClick={handleImportCSVTemplate} style={{...btnGhost, color:'#60a5fa', borderColor:'#60a5fa44'}}>⬇ CSV Template</button>
          <label style={{...btnGhost, color:'#C9A84C', borderColor:'#C9A84C44', cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6}}>
            ⬆ Import CSV
            <input type="file" accept=".csv" style={{display:'none'}} onChange={handleImportCSVUpload} />
          </label>
          <button onClick={handleExportPDF} style={btnGhost}>🖨 Print</button>
          <button onClick={openAdd} style={btnPrimary}>+ Add Contact</button>
        </div>
      </div>

      {/* FLASH */}
      {error   && <div style={{ background: 'rgba(239,68,68,0.15)', border: `1px solid ${C.red}`, borderRadius: 8, padding: '10px 16px', color: C.red, marginBottom: 16, fontSize: 13 }}>{error}</div>}
      {success && <div style={{ background: 'rgba(201,168,76,0.15)', border: `1px solid ${C.gold}`, borderRadius: 8, padding: '10px 16px', color: C.gold, marginBottom: 16, fontSize: 13 }}>{success}</div>}

      {/* STATS BAR */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Total Contacts', value: stats.total, color: C.goldLt, emoji: '📋' },
          { label: 'Active',         value: stats.active, color: '#4ade80', emoji: '✅' },
          { label: 'Diaspora',       value: stats.diaspora, color: '#a78bfa', emoji: '✈️' },
          { label: 'BOT',            value: stats.byType['BOT'] || 0, color: C.gold, emoji: '🏛️' },
          { label: 'CWC',            value: stats.byType['CWC'] || 0, color: C.teal, emoji: '⚖️' },
          { label: 'LOC',            value: stats.byType['LOC'] || 0, color: C.blue, emoji: '📋' },
          { label: 'Sponsors',       value: stats.byType['SPONSOR'] || 0, color: C.goldLt, emoji: '🤝' },
          { label: 'Vendors',        value: stats.byType['VENDOR'] || 0, color: '#fb923c', emoji: '🛒' },
        ].map(s => (
          <div key={s.label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 20 }}>{s.emoji}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* TYPE TABS */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        {[{ value: 'ALL', label: 'All Contacts', emoji: '📋', color: C.text },...CONTACT_TYPES].map(t => {
          const active = activeTab === t.value;
          const count  = t.value === 'ALL' ? contacts.length : (stats.byType[t.value] || 0);
          return (
            <button key={t.value} onClick={() => setActiveTab(t.value as TabId)}
              style={{ padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: active ? 'none' : `1px solid ${C.border}`,
                background: active ? ('color' in t ? t.color as string : C.gold) : C.card,
                color: active ? '#000' : C.muted, transition: 'all 0.2s' }}>
              {'emoji' in t && t.emoji} {t.label} ({count})
            </button>
          );
        })}
      </div>

      {/* SEARCH + FILTERS */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search name, email, phone, organisation..."
          style={{ ...inputStyle, maxWidth: 280, flex: '1 1 200px' }} />
        <select value={filterVillage} onChange={e => setFilterVillage(e.target.value)} style={{ ...inputStyle, maxWidth: 200 }}>
          <option value="">All Villages</option>
          {VILLAGES.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select value={filterActive} onChange={e => setFilterActive(e.target.value as 'all'|'active'|'inactive')} style={{ ...inputStyle, maxWidth: 140 }}>
          <option value="active">Active Only</option>
          <option value="inactive">Inactive Only</option>
          <option value="all">All Status</option>
        </select>
        <span style={{ color: C.muted, fontSize: 12 }}>{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* CONTACT GRID */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: C.muted }}>Loading contacts...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: C.muted }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <div style={{ fontSize: 16 }}>No contacts found</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>Click <strong style={{ color: C.gold }}>+ Add Contact</strong> to begin building the directory</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
          {filtered.map(c => {
            const ti = typeInfo(c.type);
            return (
              <div key={c.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px', position: 'relative', opacity: c.isActive ? 1 : 0.6 }}>
                {/* Type badge */}
                <div style={{ position: 'absolute', top: 12, right: 12, background: ti.color + '22', border: `1px solid ${ti.color}44`, borderRadius: 20, padding: '2px 10px', fontSize: 11, color: ti.color, fontWeight: 600 }}>
                  {ti.emoji} {ti.label}
                </div>
                {!c.isActive && (
                  <div style={{ position: 'absolute', top: 40, right: 12, background: C.red + '22', borderRadius: 20, padding: '2px 8px', fontSize: 10, color: C.red }}>Inactive</div>
                )}
                {/* Name */}
                <div style={{ fontFamily: "'Cinzel', serif", fontSize: 15, color: C.goldLt, marginBottom: 4, marginRight: 80 }}>
                  {c.firstName} {c.lastName}
                  {c.isDiaspora && <span style={{ marginLeft: 6, fontSize: 12 }}>✈️</span>}
                </div>
                {c.role && <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>{c.role}</div>}
                {/* Details */}
                <div style={{ fontSize: 12, color: C.text, lineHeight: 1.8 }}>
                  {c.organization && <div>🏢 {c.organization}</div>}
                  {c.email        && <div>📧 {c.email}</div>}
                  {c.phone        && <div>📞 {c.phone}</div>}
                  {c.village      && <div>🏘️ {villageName(c.village)}</div>}
                  {c.country !== 'Nigeria' && <div>🌍 {c.country}</div>}
                </div>
                {/* Tags */}
                {c.tags?.length > 0 && (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 8 }}>
                    {c.tags.map(t => (
                      <span key={t} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '1px 8px', fontSize: 10, color: C.muted }}>{t}</span>
                    ))}
                  </div>
                )}
                {/* Actions */}
                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                  <button onClick={() => setViewContact(c)} style={{ ...btnGhost, padding: '6px 14px', fontSize: 12, flex: 1 }}>👁 View</button>
                  <button onClick={() => openEdit(c)} style={{ ...btnGhost, padding: '6px 14px', fontSize: 12, flex: 1 }}>✏️ Edit</button>
                  <button onClick={() => setDeleteTarget(c)} style={{ ...btnGhost, padding: '6px 14px', fontSize: 12, color: C.red, borderColor: C.red + '44' }}>🗑</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ADD/EDIT MODAL */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, width: '100%', maxWidth: 680, maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontFamily: "'Cinzel', serif", color: C.goldLt, fontSize: 18, margin: '0 0 20px' }}>
              {editContact ? '✏️ Edit Contact' : '➕ Add New Contact'}
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {/* First Name */}
              <div>
                <label style={labelStyle}>First Name *</label>
                <input value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} style={inputStyle} placeholder="First name" />
              </div>
              {/* Last Name */}
              <div>
                <label style={labelStyle}>Last Name *</label>
                <input value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} style={inputStyle} placeholder="Last name" />
              </div>
              {/* Type */}
              <div>
                <label style={labelStyle}>Contact Type *</label>
                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value as ContactType }))} style={inputStyle}>
                  {CONTACT_TYPES.map(t => <option key={t.value} value={t.value}>{t.emoji} {t.label}</option>)}
                </select>
              </div>
              {/* Role */}
              <div>
                <label style={labelStyle}>Role / Title</label>
                <input value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} style={inputStyle} placeholder="e.g. Chairman, Secretary" />
              </div>
              {/* Email */}
              <div>
                <label style={labelStyle}>Email</label>
                <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={inputStyle} placeholder="email@example.com" type="email" />
              </div>
              {/* Phone */}
              <div>
                <label style={labelStyle}>Phone</label>
                <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} style={inputStyle} placeholder="+234 xxx xxxx xxx" />
              </div>
              {/* WhatsApp */}
              <div>
                <label style={labelStyle}>WhatsApp</label>
                <input value={form.whatsapp} onChange={e => setForm(f => ({ ...f, whatsapp: e.target.value }))} style={inputStyle} placeholder="+234 xxx xxxx xxx" />
              </div>
              {/* Organization */}
              <div>
                <label style={labelStyle}>Organisation</label>
                <input value={form.organization} onChange={e => setForm(f => ({ ...f, organization: e.target.value }))} style={inputStyle} placeholder="Company or organisation" />
              </div>
              {/* Village */}
              <div>
                <label style={labelStyle}>Village / Community</label>
                <select value={form.village} onChange={e => setForm(f => ({ ...f, village: e.target.value }))} style={inputStyle}>
                  <option value="">-- Select village --</option>
                  {VILLAGES.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
              {/* Community (free text) */}
              <div>
                <label style={labelStyle}>Community (free text)</label>
                <input value={form.community} onChange={e => setForm(f => ({ ...f, community: e.target.value }))} style={inputStyle} placeholder="Subdivision or community" />
              </div>
              {/* Address */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Address</label>
                <input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} style={inputStyle} placeholder="Street address" />
              </div>
              {/* State */}
              <div>
                <label style={labelStyle}>State (Nigeria)</label>
                <select value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} style={inputStyle}>
                  <option value="">-- Select state --</option>
                  {NIGERIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {/* Country */}
              <div>
                <label style={labelStyle}>Country</label>
                <input value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} style={inputStyle} placeholder="Nigeria" />
              </div>
              {/* Festival Year */}
              <div>
                <label style={labelStyle}>Festival Year</label>
                <input value={form.festivalYear} onChange={e => setForm(f => ({ ...f, festivalYear: parseInt(e.target.value) || 2026 }))} style={inputStyle} type="number" min={2024} max={2050} />
              </div>
              {/* Toggles */}
              <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
                  <span style={{ color: C.text }}>Active</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={form.isDiaspora} onChange={e => setForm(f => ({ ...f, isDiaspora: e.target.checked }))} />
                  <span style={{ color: C.text }}>Diaspora ✈️</span>
                </label>
              </div>
              {/* Tags */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Tags</label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input value={tagInput} onChange={e => setTagInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="Type tag + Enter" style={{ ...inputStyle, flex: 1 }} />
                  <button onClick={addTag} style={{ ...btnGhost, padding: '8px 14px' }}>Add</button>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {form.tags.map(t => (
                    <span key={t} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '2px 10px', fontSize: 12, color: C.muted, display: 'flex', alignItems: 'center', gap: 4 }}>
                      {t}
                      <button onClick={() => removeTag(t)} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
                    </span>
                  ))}
                </div>
              </div>
              {/* Notes */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Notes</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={3} placeholder="Additional notes..." style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
              <button onClick={() => setShowModal(false)} style={btnGhost}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving...' : editContact ? 'Update Contact' : 'Add Contact'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VIEW MODAL */}
      {viewContact && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}>
            {(() => {
              const ti = typeInfo(viewContact.type);
              return (
                <>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
                    <div>
                      <h2 style={{ fontFamily: "'Cinzel', serif", color: C.goldLt, fontSize: 20, margin: 0 }}>
                        {viewContact.firstName} {viewContact.lastName} {viewContact.isDiaspora ? '✈️' : ''}
                      </h2>
                      {viewContact.role && <div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>{viewContact.role}</div>}
                    </div>
                    <span style={{ background: ti.color + '22', border: `1px solid ${ti.color}44`, borderRadius: 20, padding: '4px 14px', fontSize: 12, color: ti.color }}>
                      {ti.emoji} {ti.label}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gap: 10, fontSize: 14 }}>
                    {viewContact.organization && <div style={{ display: 'flex', gap: 10 }}><span style={{ color: C.muted, minWidth: 100 }}>Organisation</span><span>{viewContact.organization}</span></div>}
                    {viewContact.email        && <div style={{ display: 'flex', gap: 10 }}><span style={{ color: C.muted, minWidth: 100 }}>Email</span><a href={`mailto:${viewContact.email}`} style={{ color: C.blue }}>{viewContact.email}</a></div>}
                    {viewContact.phone        && <div style={{ display: 'flex', gap: 10 }}><span style={{ color: C.muted, minWidth: 100 }}>Phone</span><a href={`tel:${viewContact.phone}`} style={{ color: C.blue }}>{viewContact.phone}</a></div>}
                    {viewContact.whatsapp     && <div style={{ display: 'flex', gap: 10 }}><span style={{ color: C.muted, minWidth: 100 }}>WhatsApp</span><a href={`https://wa.me/${viewContact.whatsapp.replace(/\D/g,'')}`} target="_blank" rel="noreferrer" style={{ color: '#25d366' }}>{viewContact.whatsapp}</a></div>}
                    {viewContact.village      && <div style={{ display: 'flex', gap: 10 }}><span style={{ color: C.muted, minWidth: 100 }}>Village</span><span>{villageName(viewContact.village)}</span></div>}
                    {viewContact.community    && <div style={{ display: 'flex', gap: 10 }}><span style={{ color: C.muted, minWidth: 100 }}>Community</span><span>{viewContact.community}</span></div>}
                    {viewContact.address      && <div style={{ display: 'flex', gap: 10 }}><span style={{ color: C.muted, minWidth: 100 }}>Address</span><span>{viewContact.address}</span></div>}
                    {viewContact.state        && <div style={{ display: 'flex', gap: 10 }}><span style={{ color: C.muted, minWidth: 100 }}>State</span><span>{viewContact.state}</span></div>}
                    <div style={{ display: 'flex', gap: 10 }}><span style={{ color: C.muted, minWidth: 100 }}>Country</span><span>{viewContact.country}</span></div>
                    <div style={{ display: 'flex', gap: 10 }}><span style={{ color: C.muted, minWidth: 100 }}>Status</span><span style={{ color: viewContact.isActive ? '#4ade80' : C.red }}>{viewContact.isActive ? 'Active' : 'Inactive'}</span></div>
                    <div style={{ display: 'flex', gap: 10 }}><span style={{ color: C.muted, minWidth: 100 }}>Festival</span><span>{viewContact.festivalYear}</span></div>
                    {viewContact.notes && <div style={{ display: 'flex', gap: 10 }}><span style={{ color: C.muted, minWidth: 100 }}>Notes</span><span style={{ color: C.muted }}>{viewContact.notes}</span></div>}
                    {viewContact.tags?.length > 0 && (
                      <div style={{ display: 'flex', gap: 10 }}>
                        <span style={{ color: C.muted, minWidth: 100 }}>Tags</span>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {viewContact.tags.map(t => <span key={t} style={{ background: C.surface, borderRadius: 12, padding: '1px 8px', fontSize: 11, color: C.muted }}>{t}</span>)}
                        </div>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 10 }}><span style={{ color: C.muted, minWidth: 100 }}>Added</span><span style={{ color: C.muted, fontSize: 12 }}>{new Date(viewContact.createdAt).toLocaleDateString()}</span></div>
                  </div>
                  {/* Diaspora portal link */}
                  {viewContact.isDiaspora && (
                    <div style={{ marginTop: 16, padding: '10px 14px', background: '#a78bfa22', border: '1px solid #a78bfa44', borderRadius: 8, fontSize: 13, color: '#a78bfa' }}>
                      ✈️ Diaspora contact — <a href="#diaspora" onClick={() => setViewContact(null)} style={{ color: '#a78bfa', textDecoration: 'underline' }}>Go to Diaspora Portal</a>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                    <button onClick={() => openEdit(viewContact)} style={btnGhost}>✏️ Edit</button>
                    <button onClick={() => { setDeleteTarget(viewContact); setViewContact(null); }} style={{ ...btnGhost, color: C.red, borderColor: C.red + '44' }}>🗑 Delete</button>
                    <button onClick={() => setViewContact(null)} style={btnPrimary}>Close</button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* DELETE CONFIRM MODAL */}
      {deleteTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, width: '100%', maxWidth: 420, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🗑️</div>
            <h3 style={{ color: C.red, fontFamily: "'Cinzel', serif", marginBottom: 8 }}>Delete Contact</h3>
            <p style={{ color: C.muted, fontSize: 14, marginBottom: 24 }}>
              Are you sure you want to delete <strong style={{ color: C.text }}>{deleteTarget.firstName} {deleteTarget.lastName}</strong>? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button onClick={() => setDeleteTarget(null)} style={btnGhost}>Cancel</button>
              <button onClick={handleDelete} style={btnDanger}>Delete Permanently</button>
            </div>
          </div>
        </div>
      )}

      {/* FESTIVAL YEAR FOOTER */}
      <div style={{ marginTop: 40, textAlign: 'center', color: C.muted, fontSize: 12, borderTop: `1px solid ${C.border}`, paddingTop: 20 }}>
        ERUE Festival of Uzairue Kingdom — "Uzairue Heritage: Culture, Security, Prosperity"<br />
        Contacts data persists across festival editions via PostgreSQL
      </div>
    </div>
  );
};

export default ContactsDirectory;
